import { workflowChatAvailable, listWorkflowChats } from "@/api/workflowChat";
import { WorkflowChatRealtimeClient } from "@/services/workflow-chat-realtime";
import { loadWorkflowInboxPages, workflowInboxConversation } from "@/services/workflow-chat-inbox";
import { workflowPrivateConversation } from "@/services/workflow-chat-inbox";

/** PC/H5 共用群联系人适配，不改变原真人和客服消息的读写链路。 */
export default {
  data() {
    return { workflowInboxRows: [], workflowInboxPages: [1, 1], workflowInboxMore: [false, false], workflowInboxBusy: false, workflowInboxError: "" };
  },
  computed: {
    workflowConversations() {
      return this.workflowInboxRows.map(row => workflowInboxConversation(row, (key, params) => this.$t(key, params)));
    },
  },
  watch: {
    realtimeIdentity() { this.startWorkflowInbox(); },
    "$route.query.peerUserId"() { this.openWorkflowPeerFromRoute(); },
  },
  mounted() { this.startWorkflowInbox(); this.openWorkflowPeerFromRoute(); },
  beforeDestroy() {
    this._workflowInboxVersion = (this._workflowInboxVersion || 0) + 1;
    this._workflowInboxClient?.close();
  },
  methods: {
    async openWorkflowPeerFromRoute() {
      const id = String(this.$route?.query.peerUserId || "");
      if (!/^[1-9]\d*$/.test(id)) return;
      const identity = this.realtimeIdentity;
      const name = String(this.$route.query.peerName || this.$t("messageCenter.unknownUser"));
      await this.loadChatUsers();
      if (identity !== this.realtimeIdentity || String(this.$route.query.peerUserId || "") !== id) return;
      await this.openWorkflowPrivateChat({ userId: id, displayName: name });
      const query = { ...this.$route.query }; delete query.peerUserId; delete query.peerName;
      this.$router.replace({ query });
    },
    async openWorkflowPrivateChat(member) {
      if (String(member.userId) === String(this.currentUserId)) this.ensureSelfConversation?.();
      const field = "legacyConversations" in this ? "legacyConversations" : "conversations";
      const existing = this[field].find(row => String(row.userId) === String(member.userId));
      const conversation = existing || workflowPrivateConversation(this.currentUserId, member);
      if (!existing) this[field] = [...this[field], conversation];
      await this.selectConversation(conversation);
    },
    startWorkflowInbox() {
      this._workflowInboxVersion = (this._workflowInboxVersion || 0) + 1;
      const version = this._workflowInboxVersion;
      this._workflowInboxClient?.close();
      this._workflowInboxClient = null;
      this._workflowInboxAvailable = null;
      this._workflowInboxPending = false;
      this._workflowInboxEventVersion = 0;
      this.workflowInboxRows = []; this.workflowInboxPages = [1, 1]; this.workflowInboxMore = [false, false];
      this.workflowInboxBusy = false; this.workflowInboxError = "";
      const token = String(this.$store.state.user?.userInfo?.token || "");
      if (!token || !this.currentUserId) return;
      this._workflowInboxClient = new WorkflowChatRealtimeClient({ token,
        onEvent: event => {
          if (version !== this._workflowInboxVersion) return;
          if (["resource.revoked", "resource.deleted"].includes(event.eventType)) {
            this._workflowInboxEventVersion++;
            this.workflowInboxRows = this.workflowInboxRows.filter(row => row.group.id !== event.resourceId);
            this.closeMissingWorkflowConversation(event.resourceId);
          }
        },
        onRefresh: () => this.loadWorkflowConversations(),
      });
      this._workflowInboxClient.connect();
    },
    async loadWorkflowConversations(more = false) {
      if (this.workflowInboxBusy) {
        this._workflowInboxPending = true;
        return;
      }
      const version = this._workflowInboxVersion;
      const eventVersion = this._workflowInboxEventVersion;
      if (!this.currentUserId || !this._workflowInboxClient) return;
      this.workflowInboxBusy = true;
      try {
        if (this._workflowInboxAvailable === null) {
          const enabled = await workflowChatAvailable();
          if (version !== this._workflowInboxVersion) return;
          this._workflowInboxAvailable = Boolean(enabled);
        }
        if (!this._workflowInboxAvailable) return;
        const results = await Promise.all([false, true].map((archived, index) =>
          loadWorkflowInboxPages(offset => listWorkflowChats(archived, offset), this.workflowInboxPages[index] + Number(more && this.workflowInboxMore[index]))));
        if (version !== this._workflowInboxVersion || eventVersion !== this._workflowInboxEventVersion) return;
        // 活跃群在两次查询之间归档时，后读的归档事实覆盖旧记录。
        const unique = new Map(results.flatMap(result => result.rows).map(row => [row.group.id, row]));
        this.workflowInboxRows = [...unique.values()];
        this.workflowInboxPages = results.map(result => result.pages);
        this.workflowInboxMore = results.map(result => result.hasMore);
        this.workflowInboxError = "";
        this.closeMissingWorkflowConversation();
      } catch (error) {
        if (version === this._workflowInboxVersion) this.workflowInboxError = this.$t("messageCenter.conversationsFailed");
      } finally {
        if (version === this._workflowInboxVersion) {
          this.workflowInboxBusy = false;
          if (this._workflowInboxPending) {
            this._workflowInboxPending = false;
            void this.loadWorkflowConversations();
          }
        }
      }
    },
    closeMissingWorkflowConversation(unavailableId) {
      const field = "activeConv" in this ? "activeConv" : "activeConversation";
      const current = this[field];
      if (current?.kind !== "WORKFLOW_CHAT") return;
      const next = this.workflowConversations.find(row => row.key === current.key);
      this[field] = current.groupId === unavailableId ? null
        : next || (this.workflowInboxMore.some(Boolean) ? current : null);
    },
    onWorkflowInboxScroll(event) {
      const list = event.target;
      if (list.scrollHeight - list.scrollTop - list.clientHeight < 80 && this.workflowInboxMore.some(Boolean)) void this.loadWorkflowConversations(true);
    },
    workflowConversationRead({ groupId, sequence }) {
      this.workflowInboxRows = this.workflowInboxRows.map(row => row.group.id === groupId
        ? { ...row, unreadCount: Math.max(0, row.group.lastSequence - sequence) } : row);
      this.closeMissingWorkflowConversation();
      void this.$store.dispatch("user/refreshUnreadCount").catch(() => {});
    },
  },
};
